import { Typography, useTheme } from '@mui/material';
import { CSSProperties, ReactElement } from 'react';

import { formatTokenPrice } from '../../../utils/decimal-map';

// Numeric format tokens. Adding a token here means adding a case in the
// switch below. Reviewer Item 7 added `signed-percent`.
// `price-token` (IM-1908) defers to formatTokenPrice for per-token decimals —
// the caller must pass the token base symbol via the `symbol` prop.
export type NumberFormat =
  | 'price-4dp'
  | 'price-2dp'
  | 'price-token'
  | 'percent'
  | 'integer'
  | 'bps'
  | 'gamma'
  | 'mixing'
  | 'signed-percent';

// Visual hierarchy roles. Five sizes spanning the trading-terminal density
// rhythm of the MVP:
//   headline  — first-scan cell on the page (~20px)
//   body      — default mid-density value (~14px)
//   caption   — common dense-cell value (~12px, matches Select font)
//   small     — slightly denser (~11px, matches MVP Pricing & Risk)
//   micro     — most-compact rows (~10px, matches MVP SABR sub-values)
export type NumberRole = 'headline' | 'body' | 'caption' | 'small' | 'micro';

// Semantic colour tints. Resolves against theme.palette at render time.
export type NumberColour = 'default' | 'muted' | 'positive' | 'negative' | 'accent';

interface NumberBaseProps {
  value: number | null | undefined;
  role?: NumberRole;
  colour?: NumberColour;
  placeholder?: string;
  className?: string;
}

// Discriminated on `format` so the invalid state is unrepresentable: the
// `price-token` format REQUIRES a `symbol` (formatTokenPrice needs the token to
// resolve per-token decimals, e.g. "MORPHO" → 4dp); every other format forbids
// it. `<Number format="price-token" />` with no symbol is now a compile error.
export type NumberProps = NumberBaseProps &
  (
    | { format: 'price-token'; symbol: string }
    | { format: Exclude<NumberFormat, 'price-token'>; symbol?: never }
  );

// CONVENTION: only THIS file shadows the global `Number` (the default export
// below is named `Number`). Everywhere else in the feature, `Number` refers to
// the global and may be used bare — no `globalThis.Number.` qualification is
// needed outside this module. Inside this file we alias what we need from the
// global up front, before the shadow takes effect.
const globalIsNaN = globalThis.Number.isNaN;

const intl = (opts: Intl.NumberFormatOptions) => new Intl.NumberFormat('en-GB', opts);

// Single source of truth for at-rest numeric formatting. Exported so the
// editable-number primitive can share this exact eight-case ladder for its
// at-rest branch — do not fork this logic.
export function formatValue(format: NumberFormat, v: number, symbol?: string): string {
  switch (format) {
    case 'price-4dp':
      return intl({ minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(v);
    case 'price-2dp':
      return intl({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
    case 'price-token':
      return formatTokenPrice(v, symbol ?? '');
    case 'percent':
      return intl({ style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
        v,
      );
    case 'integer':
      return intl({ maximumFractionDigits: 0 }).format(v);
    case 'bps':
      return `${intl({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v * 10000)} bps`;
    case 'gamma': {
      const a = Math.abs(v);
      const dp = a >= 1000 ? 0 : a >= 1 ? 3 : a >= 0.01 ? 4 : 6;
      return intl({ minimumFractionDigits: dp, maximumFractionDigits: dp }).format(v);
    }
    case 'mixing':
      return intl({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
    case 'signed-percent': {
      const sign = v > 0 ? '+' : '';
      const body = intl({
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(v);
      return `${sign}${body}`;
    }
    default: {
      // Exhaustiveness guard: adding a NumberFormat without a case here is a
      // compile error rather than a silent empty-string return at runtime.
      const _exhaustive: never = format;
      return _exhaustive;
    }
  }
}

const Number = ({
  value,
  format,
  symbol,
  role = 'body',
  colour = 'default',
  placeholder = '—',
  className,
}: NumberProps): ReactElement => {
  const theme = useTheme();

  const text =
    value === null || value === undefined || globalIsNaN(value)
      ? placeholder
      : formatValue(format, value, symbol);

  const colourStyle: CSSProperties = (() => {
    switch (colour) {
      case 'positive':
        return { color: theme.palette.success.main };
      case 'negative':
        return { color: theme.palette.error.main };
      case 'accent':
        return { color: theme.palette.primary.main };
      case 'muted':
        return { color: theme.palette.text.secondary };
      case 'default':
      default:
        return {};
    }
  })();

  const roleStyle: CSSProperties = (() => {
    switch (role) {
      case 'headline':
        return { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.2 };
      case 'caption':
        return { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4 };
      case 'small':
        return { fontSize: '0.6875rem', fontWeight: 400, lineHeight: 1.4 };
      case 'micro':
        return { fontSize: '0.625rem', fontWeight: 400, lineHeight: 1.3 };
      case 'body':
      default:
        return { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.4 };
    }
  })();

  return (
    <Typography
      component="span"
      variant="numeric"
      className={className}
      sx={{
        ...roleStyle,
        ...colourStyle,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </Typography>
  );
};

export default Number;
